<template>
  <woot-modal :show.sync="show" :on-close="onClose" custom-class="memo-backdrop">
    <div class="memo-modal-container">
      <h2 class="memo-title">📝 {{ $t('CONVERSATION.MEMO.TITLE') }}</h2>
      <p class="memo-description">
        {{ $t('CONVERSATION.MEMO.DESCRIPTION') }}
      </p>

      <div v-if="memoSent" class="memo-sent-notice">
        <fluent-icon icon="checkmark-circle" size="16" />
        <span>{{ $t('CONVERSATION.MEMO.SENT_NOTICE') }}</span>
      </div>

      <textarea
        v-else
        v-model="memoText"
        class="memo-textarea"
        rows="4"
        :placeholder="$t('CONVERSATION.MEMO.PLACEHOLDER')"
        :disabled="memoSent"
      />

      <div class="memo-actions">
        <woot-button
          v-if="!memoSent && hasMemo"
          color-scheme="alert"
          variant="smooth"
          size="small"
          :is-loading="isDeleting"
          @click="removeMemo"
        >
          {{ $t('CONVERSATION.MEMO.REMOVE') }}
        </woot-button>
        <div class="memo-actions-right">
          <woot-button
            color-scheme="secondary"
            variant="smooth"
            size="small"
            @click="onClose"
          >
            {{ $t('CONVERSATION.MEMO.CLOSE') }}
          </woot-button>
          <woot-button
            v-if="!memoSent"
            size="small"
            :is-loading="isSaving"
            :is-disabled="!memoText || !memoText.trim()"
            @click="saveMemo"
          >
            {{ $t('CONVERSATION.MEMO.SAVE') }}
          </woot-button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import ConversationApi from '../../../api/conversations';

export default {
  name: 'MemoModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    currentMemo: {
      type: String,
      default: '',
    },
    memoSent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      memoText: this.currentMemo || '',
      isSaving: false,
      isDeleting: false,
    };
  },
  computed: {
    hasMemo() {
      return this.currentMemo && this.currentMemo.trim().length > 0;
    },
  },
  watch: {
    currentMemo(val) {
      this.memoText = val || '';
    },
    show(val) {
      if (val) {
        this.memoText = this.currentMemo || '';
      }
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async saveMemo() {
      this.isSaving = true;
      try {
        await ConversationApi.setMemo(this.conversationId, this.memoText);
        bus.$emit('newToastMessage', this.$t('CONVERSATION.MEMO.SAVED_SUCCESS'));
        this.$emit('memo-saved', this.memoText);
        this.onClose();
      } catch (error) {
        const msg =
          error?.response?.data?.error || this.$t('CONVERSATION.MEMO.SAVE_ERROR');
        bus.$emit('newToastMessage', msg);
      } finally {
        this.isSaving = false;
      }
    },
    async removeMemo() {
      this.isDeleting = true;
      try {
        await ConversationApi.deleteMemo(this.conversationId);
        bus.$emit('newToastMessage', this.$t('CONVERSATION.MEMO.REMOVED_SUCCESS'));
        this.$emit('memo-removed');
        this.memoText = '';
        this.onClose();
      } catch (error) {
        const msg =
          error?.response?.data?.error || this.$t('CONVERSATION.MEMO.REMOVE_ERROR');
        bus.$emit('newToastMessage', msg);
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.memo-modal-container {
  padding: var(--space-normal);
}

.memo-title {
  margin-bottom: var(--space-small);
}

.memo-description {
  color: var(--s-600);
  margin-bottom: var(--space-normal);
  font-size: var(--font-size-small);
}

.memo-textarea {
  width: 100%;
  border: 1px solid var(--s-200);
  border-radius: var(--border-radius-normal);
  padding: var(--space-small);
  font-size: var(--font-size-small);
  resize: vertical;
  min-height: 80px;

  &:focus {
    border-color: var(--w-500);
    outline: none;
  }
}

.memo-sent-notice {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  background: var(--g-50);
  border-radius: var(--border-radius-normal);
  color: var(--g-800);
  font-size: var(--font-size-small);
}

.memo-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-normal);
}

.memo-actions-right {
  display: flex;
  gap: var(--space-small);
  margin-left: auto;
}
</style>

<style lang="scss">
.memo-backdrop.modal-mask {
  background-color:rgba(0, 0, 0, .4) !important;
}
</style>
